/**
 * "What counts as a piece": the locked rules (handover §1), closed by default
 * so the bill stays the first thing read. A disclosure inside a heading (the
 * accordion pattern), animated with the Collapsible keyframes (DESIGN §5,
 * 200 ms ease-out, none under reduced motion).
 *
 * The allowance sentence ("5,000 pieces ... A$0.10") shows only on a plan
 * with a piece price: on a set monthly price it would promise a charge that
 * never comes.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

interface BillingExplainerProps {
  /** The plan's allowance, or null when the plan has no piece price. */
  allowance: { included: number; price: string } | null;
}

export function BillingExplainer({ allowance }: BillingExplainerProps) {
  const { t } = useTranslation('billing');
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-lg border bg-card">
      <h3 className="text-sm font-semibold">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-3 text-left transition-colors duration-200 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none sm:px-4"
          >
            {t('explainer.title')}
            <ChevronDown
              className={cn(
                'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 motion-reduce:transition-none',
                open && 'rotate-180',
              )}
              aria-hidden="true"
            />
          </button>
        </CollapsibleTrigger>
      </h3>
      <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down motion-reduce:animate-none">
        <ul className="max-w-prose list-disc space-y-1.5 border-t py-3 pl-8 pr-3 text-sm text-muted-foreground sm:pl-9 sm:pr-4">
          <li>{t('explainer.garment')}</li>
          <li>{t('explainer.reprints')}</li>
          <li>{t('explainer.orderChange')}</li>
          <li>{t('explainer.prePrinted')}</li>
          <li>{t('explainer.clients')}</li>
          {allowance && <li>{t('explainer.allowance', allowance)}</li>}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}
